import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormularioCampo, FormularioArea } from '@/types/formulario';
import { X, Plus, Trash2 } from 'lucide-react';

interface FormularioFieldConfigProps {
  campo: FormularioCampo | null;
  areas: FormularioArea[];
  onUpdate: (updates: Partial<FormularioCampo>) => void;
  onClose: () => void;
}

export const FormularioFieldConfig = ({ campo, areas, onUpdate, onClose }: FormularioFieldConfigProps) => {
  const [label, setLabel] = useState('');
  const [descricao, setDescricao] = useState('');
  const [obrigatorio, setObrigatorio] = useState(false);
  const [areaId, setAreaId] = useState<string | null>(null);
  const [opcoes, setOpcoes] = useState<string[]>(['']);
  const [escalaMin, setEscalaMin] = useState(1);
  const [escalaMax, setEscalaMax] = useState(5);
  const [escalaRotulos, setEscalaRotulos] = useState<Record<string, string>>({
    '1': 'Discordo Totalmente',
    '2': 'Discordo Parcialmente',
    '3': 'Neutro',
    '4': 'Concordo Parcialmente',
    '5': 'Concordo Totalmente',
  });
  const [estiloVisualizacao, setEstiloVisualizacao] = useState<'texto' | 'emojis' | 'cores' | 'texto_emoji' | 'texto_cor'>('texto');
  const [escalaEmojis, setEscalaEmojis] = useState<Record<string, string>>({});
  const [escalaCores, setEscalaCores] = useState<Record<string, string>>({});
  const [tipoGradiente, setTipoGradiente] = useState<'manual' | 'automatico_vermelho_verde' | 'automatico_verde_vermelho'>('automatico_vermelho_verde');
  const [escalaInvertida, setEscalaInvertida] = useState(false);
  const [tipoEscala, setTipoEscala] = useState<'numerica' | 'texto'>('numerica');
  const [valoresTexto, setValoresTexto] = useState<string[]>(['Ruim', 'Regular', 'Bom', 'Ótimo', 'Excelente']);
  const [layoutOpcoes, setLayoutOpcoes] = useState<'vertical' | 'horizontal'>('vertical');
  
  // Estado do Plano de Ação
  const [planoAcaoConfig, setPlanoAcaoConfig] = useState<any>({
    habilitado: false,
    obrigatorio: false,
    condicao_disparo: {
      tipo: 'valor_especifico',
      valores: [],
      min: undefined,
      max: undefined,
      operador: 'igual',
    },
    config_botao: {
      label: 'Criar Plano de Ação',
      descricao: 'Esta resposta requer um plano de ação',
      cor: 'warning',
    },
  });

  useEffect(() => {
    if (campo) {
      setLabel(campo.label);
      setDescricao(campo.descricao || '');
      setObrigatorio(campo.obrigatorio);
      setAreaId(campo.area_id || null);
      
      if (campo.configuracoes?.opcoes) {
        setOpcoes(campo.configuracoes.opcoes);
      }
      
      if (campo.tipo_campo === 'escala') {
        setEscalaMin(campo.configuracoes?.minimo || 1);
        setEscalaMax(campo.configuracoes?.maximo || 5);
        setEscalaRotulos(campo.configuracoes?.rotulos || {
          '1': 'Discordo Totalmente',
          '2': 'Discordo Parcialmente',
          '3': 'Neutro',
          '4': 'Concordo Parcialmente',
          '5': 'Concordo Totalmente',
        });
        setEstiloVisualizacao(campo.configuracoes?.estilo_visualizacao || 'texto');
      setEscalaEmojis(campo.configuracoes?.emojis || {});
      setEscalaCores(campo.configuracoes?.cores || {});
      setTipoGradiente(campo.configuracoes?.tipo_gradiente || 'automatico_vermelho_verde');
      setEscalaInvertida(campo.configuracoes?.escala_invertida || false);
      setTipoEscala(campo.configuracoes?.tipo_escala || 'numerica');
      setValoresTexto(campo.configuracoes?.valores_texto || ['Ruim', 'Regular', 'Bom', 'Ótimo', 'Excelente']);
      }
      
      if (campo.tipo_campo === 'radio') {
        setLayoutOpcoes(campo.configuracoes?.layout_opcoes || 'vertical');
      }
      
      // Carregar configurações de Plano de Ação
      if (campo.configuracoes?.plano_acao) {
        setPlanoAcaoConfig(campo.configuracoes.plano_acao as any);
      }
    }
  }, [campo]);

  if (!campo) {
    return (
      <Card className="p-4 h-full flex items-center justify-center text-muted-foreground">
        Selecione um campo para configurar
      </Card>
    );
  }

  const handleSave = () => {
    const updates: Partial<FormularioCampo> = {
      label,
      descricao,
      obrigatorio,
      area_id: areaId,
    };

    if (['select', 'radio', 'checkbox'].includes(campo.tipo_campo)) {
      updates.configuracoes = {
        ...campo.configuracoes,
        opcoes: opcoes.filter(o => o.trim() !== ''),
        plano_acao: planoAcaoConfig.habilitado ? planoAcaoConfig : undefined,
      };
      
      if (campo.tipo_campo === 'radio') {
        updates.configuracoes.layout_opcoes = layoutOpcoes;
      }
    } else if (campo.tipo_campo === 'escala') {
      updates.configuracoes = {
        ...campo.configuracoes,
        tipo_escala: tipoEscala,
        valores_texto: tipoEscala === 'texto' ? valoresTexto : undefined,
        minimo: tipoEscala === 'numerica' ? escalaMin : undefined,
        maximo: tipoEscala === 'numerica' ? escalaMax : undefined,
        rotulos: tipoEscala === 'numerica' ? escalaRotulos : undefined,
        estilo_visualizacao: estiloVisualizacao,
        emojis: escalaEmojis,
        cores: escalaCores,
        tipo_gradiente: tipoGradiente,
        escala_invertida: escalaInvertida,
        plano_acao: planoAcaoConfig.habilitado ? planoAcaoConfig : undefined,
      };
    } else {
      updates.configuracoes = {
        ...campo.configuracoes,
        plano_acao: planoAcaoConfig.habilitado ? planoAcaoConfig : undefined,
      };
    }

    onUpdate(updates);
    
    // Fechar automaticamente a área de edição após salvar
    onClose();
  };

  const addOpcao = () => {
    setOpcoes([...opcoes, '']);
  };

  const updateOpcao = (index: number, value: string) => {
    const newOpcoes = [...opcoes];
    newOpcoes[index] = value;
    setOpcoes(newOpcoes);
  };

  const removeOpcao = (index: number) => {
    setOpcoes(opcoes.filter((_, i) => i !== index));
  };

  const showOpcoes = ['select', 'radio', 'checkbox'].includes(campo.tipo_campo);
  const showEscala = campo.tipo_campo === 'escala';

  const aplicarPresetEmojis = (tipo: 'feliz-triste' | 'estrelas' | 'aprovacao') => {
    const total = tipoEscala === 'numerica' ? escalaMax - escalaMin + 1 : valoresTexto.length;
    const emojis: Record<string, string> = {};
    
    if (tipo === 'feliz-triste') {
      const presets = ['😡', '😟', '😐', '🙂', '😊'];
      Array.from({ length: total }, (_, i) => {
        const chave = tipoEscala === 'numerica' ? escalaMin + i : i;
        const index = Math.floor((i / Math.max(total - 1, 1)) * (presets.length - 1));
        emojis[chave] = presets[index];
      });
    } else if (tipo === 'estrelas') {
      Array.from({ length: total }, (_, i) => {
        const chave = tipoEscala === 'numerica' ? escalaMin + i : i;
        emojis[chave] = '⭐'.repeat(Math.min(i + 1, 5));
      });
    } else if (tipo === 'aprovacao') {
      const presets = ['❌', '⚠️', '⭕', '✅'];
      Array.from({ length: total }, (_, i) => {
        const chave = tipoEscala === 'numerica' ? escalaMin + i : i;
        const index = Math.floor((i / Math.max(total - 1, 1)) * (presets.length - 1));
        emojis[chave] = presets[index];
      });
    }
    
    setEscalaEmojis(emojis);
  };

  const gerarGradienteCores = (direcao: 'vermelho_verde' | 'verde_vermelho'): Record<string, string> => {
    const cores: Record<string, string> = {};
    const total = tipoEscala === 'numerica' ? escalaMax - escalaMin + 1 : valoresTexto.length;
    
    for (let i = 0; i < total; i++) {
      const chave = tipoEscala === 'numerica' ? escalaMin + i : i;
      const percent = i / Math.max(total - 1, 1);
      
      let r, g, b;
      
      if (direcao === 'vermelho_verde') {
        if (percent < 0.5) {
          r = 220;
          g = Math.floor(59 + (187 - 59) * (percent * 2));
          b = 38;
        } else {
          r = Math.floor(220 - (220 - 22) * ((percent - 0.5) * 2));
          g = Math.floor(187 + (163 - 187) * ((percent - 0.5) * 2));
          b = Math.floor(38 + (74 - 38) * ((percent - 0.5) * 2));
        }
      } else {
        const invertPercent = 1 - percent;
        if (invertPercent < 0.5) {
          r = 220;
          g = Math.floor(59 + (187 - 59) * (invertPercent * 2));
          b = 38;
        } else {
          r = Math.floor(220 - (220 - 22) * ((invertPercent - 0.5) * 2));
          g = Math.floor(187 + (163 - 187) * ((invertPercent - 0.5) * 2));
          b = Math.floor(38 + (74 - 38) * ((invertPercent - 0.5) * 2));
        }
      }
      
      cores[chave] = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }
    
    return cores;
  };

  return (
    <Card className="p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Configurar Campo</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto">
        <div>
          <Label htmlFor="label">Rótulo do Campo *</Label>
          <Input
            id="label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ex: Nome completo"
          />
        </div>

        <div>
          <Label htmlFor="descricao">Descrição (opcional)</Label>
          <Textarea
            id="descricao"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Texto de ajuda para o usuário"
            rows={3}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="obrigatorio"
            checked={obrigatorio}
            onCheckedChange={setObrigatorio}
          />
          <Label htmlFor="obrigatorio">Campo obrigatório</Label>
        </div>

        <div>
          <Label htmlFor="area">Área / Seção</Label>
          <Select value={areaId || 'sem-area'} onValueChange={(value) => setAreaId(value === 'sem-area' ? null : value)}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione uma área" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sem-area">(Sem área)</SelectItem>
              {areas.map((area) => (
                <SelectItem key={area.id} value={area.id}>
                  {area.titulo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showOpcoes && (
          <div>
            <Label>Opções</Label>
            <div className="space-y-2 mt-2">
              {opcoes.map((opcao, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={opcao}
                    onChange={(e) => updateOpcao(index, e.target.value)}
                    placeholder={`Opção ${index + 1}`}
                  />
                  {opcoes.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeOpcao(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={addOpcao}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Opção
              </Button>
            </div>
          </div>
        )}

        {campo.tipo_campo === 'radio' && (
          <div className="space-y-2">
            <Label>Layout das Opções</Label>
            <Select value={layoutOpcoes} onValueChange={(value: 'vertical' | 'horizontal') => setLayoutOpcoes(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vertical">
                  ⬇️ Vertical (uma abaixo da outra)
                </SelectItem>
                <SelectItem value="horizontal">
                  ➡️ Horizontal (em linha)
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Horizontal é recomendado para escalas numéricas (ex: 1-10)
            </p>
          </div>
        )}

        {showEscala && (
          <div className="space-y-4">
            <div>
              <Label>Tipo de Escala</Label>
              <Select value={tipoEscala} onValueChange={(value: any) => setTipoEscala(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="numerica">🔢 Numérica (1, 2, 3...)</SelectItem>
                  <SelectItem value="texto">📝 Texto Customizado (Ruim, Bom...)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {tipoEscala === 'numerica' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="escalaMin">Valor Mínimo</Label>
                  <Input
                    id="escalaMin"
                    type="number"
                    value={escalaMin}
                    onChange={(e) => setEscalaMin(Number(e.target.value))}
                    min={1}
                  />
                </div>
                <div>
                  <Label htmlFor="escalaMax">Valor Máximo</Label>
                  <Input
                    id="escalaMax"
                    type="number"
                    value={escalaMax}
                    onChange={(e) => setEscalaMax(Number(e.target.value))}
                    max={10}
                  />
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Valores da Escala</Label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setValoresTexto([...valoresTexto, ''])}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Adicionar Valor
                  </Button>
                </div>
                <div className="space-y-2">
                  {valoresTexto.map((valor, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-sm font-medium w-8">{index + 1}:</span>
                      <Input
                        value={valor}
                        onChange={(e) => {
                          const novosValores = [...valoresTexto];
                          novosValores[index] = e.target.value;
                          setValoresTexto(novosValores);
                        }}
                        placeholder={`Valor ${index + 1}`}
                      />
                      {valoresTexto.length > 2 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const novosValores = valoresTexto.filter((_, i) => i !== index);
                            setValoresTexto(novosValores);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Mínimo 2 valores. Exemplos: Ruim, Regular, Bom, Ótimo, Excelente
                </p>
              </div>
            )}

            <div>
              <Label>Estilo de Visualização</Label>
              <Select value={estiloVisualizacao} onValueChange={(value: any) => setEstiloVisualizacao(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="texto">📝 Apenas Texto</SelectItem>
                  <SelectItem value="emojis">😊 Apenas Emojis</SelectItem>
                  <SelectItem value="cores">🎨 Apenas Cores</SelectItem>
                  <SelectItem value="texto_emoji">📝😊 Texto + Emoji</SelectItem>
                  <SelectItem value="texto_cor">📝🎨 Texto + Cor de Fundo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex-1">
                <Label htmlFor="escala-invertida" className="text-sm font-medium cursor-pointer">
                  🔄 Inverter Escala
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Use quando menor é melhor (ex: nível de dor, dificuldade). O valor mínimo receberá as características visuais do máximo e vice-versa.
                </p>
              </div>
              <Switch
                id="escala-invertida"
                checked={escalaInvertida}
                onCheckedChange={setEscalaInvertida}
              />
            </div>

              {tipoEscala === 'numerica' && (estiloVisualizacao === 'texto' || 
                estiloVisualizacao === 'texto_emoji' || 
                estiloVisualizacao === 'texto_cor') && (
                <div>
                  <Label>Rótulos da Escala</Label>
                  <div className="space-y-2 mt-2">
                    {Array.from({ length: escalaMax - escalaMin + 1 }, (_, i) => {
                      const valor = escalaMin + i;
                      return (
                        <div key={valor} className="flex items-center gap-2">
                          <span className="text-sm font-medium w-8">{valor}:</span>
                          <Input
                            value={escalaRotulos[valor] || ''}
                            onChange={(e) => setEscalaRotulos({ ...escalaRotulos, [valor]: e.target.value })}
                            placeholder={`Rótulo para ${valor}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {(estiloVisualizacao === 'emojis' || estiloVisualizacao === 'texto_emoji') && (
              <div>
                <Label>Emojis da Escala</Label>
                <div className="space-y-2 mt-2">
                  {(tipoEscala === 'numerica' 
                    ? Array.from({ length: escalaMax - escalaMin + 1 }, (_, i) => escalaMin + i)
                    : valoresTexto.map((_, i) => i)
                  ).map((chave, index) => {
                    const labelTexto = tipoEscala === 'texto' ? valoresTexto[index] : escalaRotulos[chave] || `Valor ${chave}`;
                    return (
                      <div key={chave} className="flex items-center gap-2">
                        <span className="text-sm font-medium w-8">{tipoEscala === 'texto' ? index + 1 : chave}:</span>
                        <Input
                          value={escalaEmojis[chave] || ''}
                          onChange={(e) => setEscalaEmojis({ ...escalaEmojis, [chave]: e.target.value })}
                          placeholder="Ex: 😊"
                          maxLength={4}
                          className="w-24 text-2xl text-center"
                        />
                        <span className="text-xs text-muted-foreground truncate flex-1">
                          {labelTexto}
                        </span>
                      </div>
                    );
                  })}
                </div>
                
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => aplicarPresetEmojis('feliz-triste')}
                  >
                    😊→😢 Feliz/Triste
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => aplicarPresetEmojis('estrelas')}
                  >
                    ⭐ Estrelas
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => aplicarPresetEmojis('aprovacao')}
                  >
                    ✅→❌ Aprovação
                  </Button>
                </div>
              </div>
            )}

            {(estiloVisualizacao === 'cores' || estiloVisualizacao === 'texto_cor') && (
              <div>
                <Label>Sistema de Cores</Label>
                <Select value={tipoGradiente} onValueChange={(value: any) => {
                  setTipoGradiente(value);
                  if (value !== 'manual') {
                    setEscalaCores(gerarGradienteCores(
                      value === 'automatico_vermelho_verde' ? 'vermelho_verde' : 'verde_vermelho'
                    ));
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="automatico_vermelho_verde">
                      🔴→🟢 Vermelho → Verde (Negativo → Positivo)
                    </SelectItem>
                    <SelectItem value="automatico_verde_vermelho">
                      🟢→🔴 Verde → Vermelho (Positivo → Negativo)
                    </SelectItem>
                    <SelectItem value="manual">🎨 Escolher cores manualmente</SelectItem>
                  </SelectContent>
                </Select>
                
                {tipoGradiente === 'manual' && (
                  <div className="space-y-2 mt-2">
                    {(tipoEscala === 'numerica' 
                      ? Array.from({ length: escalaMax - escalaMin + 1 }, (_, i) => escalaMin + i)
                      : valoresTexto.map((_, i) => i)
                    ).map((chave, index) => {
                      const labelTexto = tipoEscala === 'texto' ? valoresTexto[index] : escalaRotulos[chave] || `Valor ${chave}`;
                      return (
                        <div key={chave} className="flex items-center gap-2">
                          <span className="text-sm font-medium w-8">{tipoEscala === 'texto' ? index + 1 : chave}:</span>
                          <input
                            type="color"
                            value={escalaCores[chave] || '#6b7280'}
                            onChange={(e) => setEscalaCores({ ...escalaCores, [chave]: e.target.value })}
                            className="h-10 w-20 rounded cursor-pointer border"
                          />
                          <span className="text-xs text-muted-foreground truncate flex-1">
                            {labelTexto}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {tipoGradiente !== 'manual' && (
                  <div className="mt-2 p-3 bg-muted rounded-md">
                    <p className="text-sm text-muted-foreground">
                      As cores serão aplicadas automaticamente em gradiente de {
                        tipoGradiente === 'automatico_vermelho_verde' 
                          ? 'Vermelho (negativo) → Amarelo (neutro) → Verde (positivo)'
                          : 'Verde (positivo) → Amarelo (neutro) → Vermelho (negativo)'
                      }
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Configuração de Plano de Ação */}
      <div className="border-t pt-4 space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="plano-acao-enable" className="text-base font-semibold">
            Plano de Ação Condicional
          </Label>
          <Switch
            id="plano-acao-enable"
            checked={planoAcaoConfig.habilitado}
            onCheckedChange={(checked) => setPlanoAcaoConfig({ 
              ...planoAcaoConfig, 
              habilitado: checked 
            })}
          />
        </div>
        
        {planoAcaoConfig.habilitado && (
          <div className="space-y-4 pl-4 border-l-2 border-primary/30">
            <div className="flex items-center space-x-2">
              <Switch
                id="plano-obrigatorio"
                checked={planoAcaoConfig.obrigatorio}
                onCheckedChange={(checked) => setPlanoAcaoConfig({
                  ...planoAcaoConfig,
                  obrigatorio: checked
                })}
              />
              <Label htmlFor="plano-obrigatorio">Plano obrigatório quando disparado</Label>
            </div>
            
            {/* Condição de Disparo */}
            <div>
              <Label>Disparar quando:</Label>
              <Select
                value={planoAcaoConfig.condicao_disparo.tipo}
                onValueChange={(value) => setPlanoAcaoConfig({
                  ...planoAcaoConfig,
                  condicao_disparo: { ...planoAcaoConfig.condicao_disparo, tipo: value as any }
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="valor_especifico">Valor específico</SelectItem>
                  <SelectItem value="opcao_selecionada">Opção selecionada</SelectItem>
                  <SelectItem value="range">Faixa de valores</SelectItem>
                  <SelectItem value="pattern">Contém texto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Valores específicos (para tipo valor_especifico ou opcao_selecionada) */}
            {(planoAcaoConfig.condicao_disparo.tipo === 'valor_especifico' || 
              planoAcaoConfig.condicao_disparo.tipo === 'opcao_selecionada' ||
              planoAcaoConfig.condicao_disparo.tipo === 'pattern') && (
              <div>
                <Label>Valores que disparam</Label>
                <Textarea
                  value={planoAcaoConfig.condicao_disparo.valores?.join(', ') || ''}
                  onChange={(e) => setPlanoAcaoConfig({
                    ...planoAcaoConfig,
                    condicao_disparo: {
                      ...planoAcaoConfig.condicao_disparo,
                      valores: e.target.value.split(',').map(v => v.trim())
                    }
                  })}
                  placeholder="Digite os valores separados por vírgula. Ex: Não, Crítico, Reprovado"
                  rows={2}
                />
              </div>
            )}
            
            {/* Range (para tipo range) */}
            {planoAcaoConfig.condicao_disparo.tipo === 'range' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valor mínimo</Label>
                  <Input
                    type="number"
                    value={planoAcaoConfig.condicao_disparo.min || ''}
                    onChange={(e) => setPlanoAcaoConfig({
                      ...planoAcaoConfig,
                      condicao_disparo: {
                        ...planoAcaoConfig.condicao_disparo,
                        min: Number(e.target.value)
                      }
                    })}
                  />
                </div>
                <div>
                  <Label>Valor máximo</Label>
                  <Input
                    type="number"
                    value={planoAcaoConfig.condicao_disparo.max || ''}
                    onChange={(e) => setPlanoAcaoConfig({
                      ...planoAcaoConfig,
                      condicao_disparo: {
                        ...planoAcaoConfig.condicao_disparo,
                        max: Number(e.target.value)
                      }
                    })}
                  />
                </div>
              </div>
            )}
            
            {/* Configuração do Botão */}
            <div>
              <Label>Texto do botão</Label>
              <Input
                value={planoAcaoConfig.config_botao.label}
                onChange={(e) => setPlanoAcaoConfig({
                  ...planoAcaoConfig,
                  config_botao: { ...planoAcaoConfig.config_botao, label: e.target.value }
                })}
                placeholder="Ex: Criar Plano de Ação"
              />
            </div>
            
            <div>
              <Label>Descrição do botão</Label>
              <Input
                value={planoAcaoConfig.config_botao.descricao || ''}
                onChange={(e) => setPlanoAcaoConfig({
                  ...planoAcaoConfig,
                  config_botao: { ...planoAcaoConfig.config_botao, descricao: e.target.value }
                })}
                placeholder="Ex: Esta resposta requer um plano de ação"
              />
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t">
        <Button onClick={handleSave} className="w-full">
          Salvar Configurações
        </Button>
      </div>
    </Card>
  );
};
